import type {
  Network,
  ProtocolParameters,
  Provider,
  Transaction,
  TxHash,
  WalletApi,
} from "@lucid-evolution/lucid";

import { stripHexPrefix } from "./bytes";

const CARDANO_DISCOVERY_POLL_MS = 1_500;

export interface CardanoWallet {
  id: string;
  name: string;
  icon?: string;
  enable(): Promise<WalletApi>;
  isEnabled?(): Promise<boolean>;
}

interface ActiveCardanoWallet {
  id: string;
  name: string;
  api: WalletApi;
}

let activeWallet: ActiveCardanoWallet | null = null;

export function currentCardanoWallet(): ActiveCardanoWallet | null {
  return activeWallet;
}

export function clearCardanoWallet(): void {
  activeWallet = null;
}

export function subscribeCardanoWallets(onChange: (wallets: CardanoWallet[]) => void): () => void {
  const publish = () => {
    onChange(availableCardanoWallets());
  };

  publish();
  const interval = window.setInterval(publish, CARDANO_DISCOVERY_POLL_MS);
  window.addEventListener("focus", publish);

  return () => {
    window.clearInterval(interval);
    window.removeEventListener("focus", publish);
  };
}

export function availableCardanoWallets(): CardanoWallet[] {
  const cardano = (window as Window & { cardano?: Record<string, unknown> }).cardano;
  if (cardano === undefined) {
    return [];
  }

  return Object.entries(cardano)
    .flatMap(([id, candidate]) => {
      if (!isCardanoWallet(candidate)) {
        return [];
      }
      return [
        {
          id,
          name: candidate.name,
          icon: candidate.icon,
          enable: candidate.enable.bind(candidate),
          isEnabled: candidate.isEnabled?.bind(candidate),
        },
      ];
    })
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" }));
}

export async function connectCardanoWallet(wallet: CardanoWallet): Promise<{
  address: string;
  networkId: number;
}> {
  const api = await wallet.enable();
  activeWallet = {
    id: wallet.id,
    name: wallet.name,
    api,
  };

  return cardanoWalletSnapshot();
}

export async function cardanoWalletSnapshot(): Promise<{ address: string; networkId: number }> {
  if (activeWallet === null) {
    throw new Error("No Cardano wallet is connected");
  }

  const [addressHex, networkId] = await Promise.all([
    activeWallet.api.getChangeAddress(),
    activeWallet.api.getNetworkId(),
  ]);
  const { CML } = await import("@lucid-evolution/lucid");
  const address = CML.Address.from_hex(addressHex).to_bech32(undefined);
  return { address, networkId };
}

export async function submitCardanoSendAction(
  network: string,
  action: { to: string; value: string; data: string },
): Promise<string> {
  if (activeWallet === null) {
    throw new Error("No Cardano wallet is connected");
  }

  const { Lucid, PROTOCOL_PARAMETERS_DEFAULT } = await import("@lucid-evolution/lucid");
  const lucidNetwork = toLucidNetwork(network);
  const lucid = await Lucid(
    cardanoProvider(activeWallet.api, PROTOCOL_PARAMETERS_DEFAULT),
    lucidNetwork,
    {
      presetProtocolParameters: PROTOCOL_PARAMETERS_DEFAULT,
    },
  );
  lucid.selectWallet.fromAPI(activeWallet.api);

  const transaction = await lucid
    .newTx()
    .pay.ToAddressWithData(
      action.to,
      { kind: "inline", value: stripHexPrefix(action.data) },
      parseCardanoActionValue(action.value),
    )
    .complete();
  const signed = await transaction.sign.withWallet().complete();
  return signed.submit();
}

function parseCardanoActionValue(value: string): Record<string, bigint> {
  const parts = value
    .split("+")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length === 0) {
    throw new Error("Cardano value is empty");
  }

  const assets: Record<string, bigint> = {};
  const lovelaceText = parts[0];
  if (lovelaceText === undefined) {
    throw new Error("Cardano value is empty");
  }
  const lovelace = parseCardanoQuantity(lovelaceText, "lovelace");
  assets.lovelace = lovelace;

  for (const part of parts.slice(1)) {
    const [quantityText, assetId, extra] = part.split(/\s+/u);
    if (quantityText === undefined || assetId === undefined || extra !== undefined) {
      throw new Error("Cardano value asset entries must be '<quantity> <asset>'");
    }
    const asset = assetId.toLowerCase();
    if (
      asset.length < 56 ||
      asset.length > 120 ||
      asset.length % 2 !== 0 ||
      !/^[0-9a-f]+$/u.test(asset)
    ) {
      throw new Error("Cardano asset ID must be policy ID plus asset-name hex");
    }
    const quantity = parseCardanoQuantity(quantityText, asset);
    if (quantity === 0n) {
      throw new Error("Cardano asset quantity must be greater than zero");
    }
    assets[asset] = (assets[asset] ?? 0n) + quantity;
  }

  return assets;
}

function parseCardanoQuantity(value: string, label: string): bigint {
  if (!/^[0-9]+$/u.test(value)) {
    throw new Error(`Cardano ${label} quantity must be a decimal integer`);
  }
  return BigInt(value);
}

function cardanoProvider(api: WalletApi, protocolParameters: ProtocolParameters): Provider {
  return {
    getProtocolParameters: async (): Promise<ProtocolParameters> => protocolParameters,
    getUtxos: unsupportedProviderMethod("Cardano wallet UTxO lookup is handled by CIP-30"),
    getUtxosWithUnit: unsupportedProviderMethod("Cardano wallet UTxO lookup is handled by CIP-30"),
    getUtxoByUnit: unsupportedProviderMethod("Cardano wallet UTxO lookup is handled by CIP-30"),
    getUtxosByOutRef: unsupportedProviderMethod("Cardano wallet UTxO lookup is handled by CIP-30"),
    getDelegation: unsupportedProviderMethod("Delegation lookup is not used by Smart Wallet"),
    getDatum: unsupportedProviderMethod("Datum lookup is not used by Smart Wallet sends"),
    awaitTx: unsupportedProviderMethod("Transaction confirmation is handled by the wallet"),
    submitTx: async (tx: Transaction): Promise<TxHash> => api.submitTx(tx),
    evaluateTx: unsupportedProviderMethod("Local evaluation is not used by Smart Wallet sends"),
  };
}

function unsupportedProviderMethod<TArgs extends unknown[], TResult>(
  message: string,
): (..._args: TArgs) => Promise<TResult> {
  return async () => {
    throw new Error(message);
  };
}

function toLucidNetwork(network: string): Network {
  switch (network.toLowerCase()) {
    case "mainnet":
      return "Mainnet";
    case "preview":
      return "Preview";
    case "preprod":
    case "testnet":
      return "Preprod";
    default:
      return "Custom";
  }
}

function isCardanoWallet(candidate: unknown): candidate is {
  name: string;
  icon?: string;
  enable: () => Promise<WalletApi>;
  isEnabled?: () => Promise<boolean>;
} {
  if (candidate === null || typeof candidate !== "object") {
    return false;
  }

  const record = candidate as Record<string, unknown>;
  return typeof record.name === "string" && typeof record.enable === "function";
}
